/* =============================================================================
 * Biometric decisions that are just logic
 * -----------------------------------------------------------------------------
 * No imports, so this can be tested without a handset or the Capacitor runtime.
 * Both functions here were written from real device behaviour and both are the
 * kind of thing that quietly rots.
 * ========================================================================== */

/**
 * Mirrors the plugin's BiometryType enum.
 *
 * Duplicated as plain numbers rather than imported so this module stays free of
 * Capacitor — biometric.ts asserts at compile time that the two agree, the same
 * arrangement policy.ts uses to keep its schema and its interface in step.
 */
export const BIOMETRY = {
  NONE: 0,
  TOUCH_ID: 1,
  FACE_ID: 2,
  FINGERPRINT: 3,
  FACE_AUTHENTICATION: 4,
  IRIS_AUTHENTICATION: 5,
  MULTIPLE: 6,
  DEVICE_CREDENTIAL: 7,
} as const;

export type BiometricLabel =
  | 'fingerprint'
  | 'face'
  | 'fingerprint or face'
  | 'biometrics';

/**
 * Wording only. It does not choose the method, and cannot.
 *
 * Android's BiometricPrompt decides for itself which enrolled biometric to
 * offer: the app asks for "a biometric of at least this strength" and the
 * system presents whatever the owner has set up. Face sign-in is therefore not
 * a separate feature — on a handset whose face unlock qualifies, the same
 * prompt uses it, and this label is the only part that differs.
 */
export function labelForBiometryType(type: number): BiometricLabel {
  switch (type) {
    case BIOMETRY.FACE_ID:
    case BIOMETRY.FACE_AUTHENTICATION:
      return 'face';
    case BIOMETRY.TOUCH_ID:
    case BIOMETRY.FINGERPRINT:
      return 'fingerprint';
    case BIOMETRY.MULTIPLE:
      // Both enrolled and both usable. Naming one would be arbitrary, and
      // wrong half the time.
      return 'fingerprint or face';
    default:
      // NONE, IRIS, DEVICE_CREDENTIAL, or a handset that will not say.
      // "Biometrics" is vaguer and never wrong, which beats promising a reader
      // that is not there.
      return 'biometrics';
  }
}

/**
 * Did the user press Cancel, or did something break?
 *
 * This decides whether an error is shown at all. Telling somebody
 * "authentication failed" after they deliberately tapped Cancel reads as a
 * broken app, and it is the single most common way a biometric flow feels
 * unreliable when it is working perfectly.
 *
 * Android reports it inconsistently across manufacturers, so both the
 * documented codes — 10 (user cancelled) and 13 (negative button) — and the
 * wording are matched.
 */
export function isCancellation(message: string): boolean {
  if (!message) return false;
  return /cancel|negative button|\buser.?cancel|(^|\D)(10|13)(\D|$)/i.test(message);
}
